import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MessageDialog from "@/components/admin/MessageDialog";
import NotesPanel from "@/components/admin/NotesPanel";
import { REQUEST_STATUSES, scoreColor, STATUS_COLORS, titleCase, fmtDate, money } from "@/lib/format";
import { toast } from "sonner";
import { Search, CheckSquare } from "lucide-react";

function Detail({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (<div className="py-1.5 border-b border-[var(--border-lch)]"><div className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{label}</div><div className="text-sm text-[var(--text)]">{String(value)}</div></div>);
}

function ageDays(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const d = Math.max(0, Math.floor(ms / 86400000));
  return d === 0 ? "today" : `${d}d`;
}

function trunc(s, n = 36) {
  if (!s) return "—";
  const t = String(s);
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

export default function AdminRequests() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  // Default = Open queue (new_request + in_progress + sourced)
  const [status, setStatus] = useState("open");
  const [sort, setSort] = useState("newest");
  const [hasOpenTask, setHasOpenTask] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status === "open") params.open = 1;
    else if (status !== "all") params.status = status;
    if (sort === "score") params.sort = "score";
    const r = await api.get("/admin/watch-requests", { params });
    setItems(r.data);
    setSelected((s) => (s ? r.data.find((x) => x.id === s.id) || null : null));
  }, [q, status, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (!selected) { setHasOpenTask(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/tasks", { params: { status: "open" } });
        if (!cancelled) {
          setHasOpenTask((r.data || []).some(
            (t) => t.related_type === "watch_request" && t.related_id === selected.id,
          ));
        }
      } catch {
        if (!cancelled) setHasOpenTask(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const patch = async (url, body, msg) => { try { await api.patch(url, body); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const addNote = async (note) => { await api.post(`/admin/watch-requests/${selected.id}/note`, { note }); toast.success("Note added"); load(); };
  const createTask = async (r) => {
    if (hasOpenTask) { toast.message("Open related task already exists"); return; }
    try {
      await api.post("/admin/tasks", {
        title: `Source: ${r.brand_wanted || "watch"} for ${r.full_name}`,
        related_type: "watch_request", related_id: r.id, related_label: r.full_name,
        priority: r.lead_score >= 70 ? "high" : "medium",
      });
      toast.success("Task created");
      setHasOpenTask(true);
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-requests">
      <div className="flex items-center gap-2 mb-1"><Search className="text-blue-600" size={22} /><h1 className="text-2xl font-semibold">Watch Requests</h1></div>
      <p className="text-sm text-[var(--text-2)] mb-4">{items.length} requests · LCH queue</p>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Search name, email, brand…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-xs bg-[var(--panel)]" data-testid="request-search" />
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="sm:w-52 bg-[var(--panel)]" data-testid="request-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            {REQUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}
          </SelectContent></Select>
        <Select value={sort} onValueChange={setSort}><SelectTrigger className="sm:w-44 bg-[var(--panel)]" data-testid="request-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="score">Score desc</SelectItem>
          </SelectContent></Select>
      </div>
      <Card className="border-[var(--border-lch)] overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase"><tr>
          <th className="text-left p-3">Requester</th><th className="text-left p-3">Wants</th>
          <th className="text-left p-3">Readiness</th><th className="text-left p-3">Deposit</th>
          <th className="text-left p-3">Budget</th>
          <th className="text-left p-3">Score</th><th className="text-left p-3">Status</th>
          <th className="text-left p-3">Age</th></tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel-2)] cursor-pointer" onClick={() => setSelected(r)} data-testid={`request-row-${r.id}`}>
              <td className="p-3"><div className="font-medium">{r.full_name}</div><div className="text-xs text-[var(--muted)]">{r.email}</div></td>
              <td className="p-3 text-[var(--text)]">{[r.brand_wanted, r.model_wanted].filter(Boolean).join(" ") || "—"}</td>
              <td className="p-3 text-[var(--text-2)]">{r.buying_readiness || "—"}</td>
              <td className="p-3 text-[var(--text-2)]" title={r.deposit_willingness || ""}>{trunc(r.deposit_willingness)}</td>
              <td className="p-3 text-[var(--text-2)]">{money(r.budget_max) || money(r.budget_min) || "—"}</td>
              <td className="p-3"><Badge variant="outline" className={scoreColor(r.lead_score)}>{r.lead_score}</Badge></td>
              <td className="p-3"><Badge variant="outline" className={STATUS_COLORS[r.status]}>{titleCase(r.status)}</Badge></td>
              <td className="p-3 text-[var(--text-2)]">{ageDays(r.created_at)}</td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={8} className="p-8 text-center text-[var(--muted)]">No requests found.</td></tr>}
        </tbody></table></div></Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{selected.full_name}<Badge variant="outline" className={scoreColor(selected.lead_score)}>Score {selected.lead_score}</Badge></DialogTitle></DialogHeader>
            <div className="flex flex-wrap gap-2 mb-3">
              <MessageDialog channel="email" to={selected.email} relatedType="watch_request" relatedId={selected.id} label="Email" />
              <MessageDialog channel="sms" to={selected.phone} relatedType="watch_request" relatedId={selected.id} label="Text" />
              <Button size="sm" variant="outline" disabled={hasOpenTask} onClick={() => createTask(selected)} data-testid="request-create-task">
                <CheckSquare size={14} className="mr-1" />{hasOpenTask ? "Task exists" : "Create Task"}
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <div>
                <Detail label="Email" value={selected.email} /><Detail label="Phone" value={selected.phone} />
                <Detail label="Location" value={selected.location} /><Detail label="Buying Readiness" value={selected.buying_readiness} />
                <Detail label="Brand" value={selected.brand_wanted} /><Detail label="Model" value={selected.model_wanted} />
                <Detail label="Reference" value={selected.reference_number} /><Detail label="Item Type" value={selected.item_type} />
              </div>
              <div>
                <Detail label="Budget Min" value={money(selected.budget_min)} /><Detail label="Budget Max" value={money(selected.budget_max)} />
                <Detail label="Box/Papers Required" value={selected.box_papers_required === null ? null : (selected.box_papers_required ? "Yes" : "No")} />
                <Detail label="Deposit Willingness" value={selected.deposit_willingness} />
                <Detail label="Authentication" value={selected.authentication_expectations} />
                <Detail label="Need By" value={selected.need_by_date} />
                <Detail label="Special Requests" value={selected.special_requests} />
                <Detail label="Age" value={ageDays(selected.created_at)} />
              </div>
            </div>
            <div className="mt-4 space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Status</Label>
              <Select value={selected.status} onValueChange={(v) => patch(`/admin/watch-requests/${selected.id}/status`, { status: v }, "Status updated")}>
                <SelectTrigger className="bg-[var(--panel)]" data-testid="request-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REQUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(selected.status_history || []).length > 0 && (
              <div className="mt-4">
                <Label className="text-xs text-[var(--text-2)] mb-2 block">Status history</Label>
                <ul className="space-y-1 text-xs text-[var(--text-2)]">
                  {[...(selected.status_history || [])].reverse().map((h, i) => (
                    <li key={`${h.at}-${i}`} className="border-b border-[var(--border-lch)] py-1">
                      {titleCase(h.from || "—")} → {titleCase(h.to)} · {fmtDate(h.at)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4"><Label className="text-xs text-[var(--text-2)] mb-2 block">Internal Notes</Label><NotesPanel notes={selected.notes_log} onAdd={addNote} /></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
